import {
  ALL_CHART_TYPES,
  IS_STACKED_BAR_HORIZONTAL_SCREENSHOT
} from "constants/chart.constants";
import { stripAxisLegendSuffix } from "entities/charts/eva/translators/mpcAxisLegend";
import { useDashboardScreenshotStore } from "stores/dashboardScreenshot/useDashboardScreenshotStore";
import { isRigFracActivityRequestChartType } from "types/charts/requests";
import {
  getSortedXAxisIndices,
  getSortedYAxisIndices
} from "utils/chart/getSortedAxisIndices";

import { WidgetComponentType } from "models/screenshot";

import { AxisSettingsList, ScreenshotSetting } from "components/screenshot/components";
import type { AxisEntry } from "components/screenshot/components";
import { WidgetBorderToggle } from "components/screenshot/components/WidgetBorderToggle";
import XLabelRotationSelector from "components/screenshot/components/XLabelRotationSelector";
import {
  SettingsControl,
  SettingsGroups,
  useScreenshotContext
} from "components/screenshot/hooks";
import { fontFamilyControl } from "components/screenshot/models/sharedControlDefs";
import { buildControls } from "components/screenshot/utils/buildScreenshotControls";
import { useUserSettings } from "components/user/hooks";

import { CHART_WIDGET_TYPES } from "./chartWidgetTypes";

/**
 * Activity vs Time's fixed axis shape for the per-axis screenshot controls
 * (AxisSettingsList): one x-axis (Time) plus the primary Rig Count y-axis
 * (layout.yAxis), and the secondary Frac Crew Count y-axis (layout.yAxis2[0])
 * ONLY when the overlay line is actually drawn. Built explicitly rather than
 * from the multi-product options/layout indexing because this chart keeps its
 * primary on `yAxis` and only the secondary on `yAxis2` — and the translator
 * hides the right axis (show: hasLine) when the response has no line series, so
 * without this gate the panel would offer inert controls for an absent axis.
 */
function buildRigFracActivityAxes(
  layout?: {
    xAxis?: { title?: string };
    yAxis?: { title?: string };
    yAxis2?: Array<{ title?: string }>;
  },
  hasSecondaryAxis = false
): { xAxes: AxisEntry[]; yAxes: AxisEntry[] } {
  const yAxes: AxisEntry[] = [
    {
      label: layout?.yAxis?.title ?? "",
      isPrimary: true,
      settingsKey: "yAxisTitleText",
      gridMajorKey: "gridShowYLines",
      gridMinorKey: "gridShowYMinorLines"
    }
  ];
  if (hasSecondaryAxis) {
    yAxes.push({
      label: layout?.yAxis2?.[0]?.title ?? "",
      isPrimary: false,
      settingsKey: "yAxis2TitleText",
      gridMajorKey: "gridShowY2Lines",
      gridMinorKey: "gridShowY2MinorLines"
    });
  }
  return {
    xAxes: [
      {
        // Fall back to "" (not the magic "Time") so it tracks the backend label
        // and matches the multi-product path's empty fallback.
        label: layout?.xAxis?.title ?? "",
        isPrimary: true,
        settingsKey: "xAxisTitleText",
        gridMajorKey: "gridShowXLines",
        gridMinorKey: "gridShowXMinorLines"
      }
    ],
    yAxes
  };
}

function useChartScreenshotSettingsControls(): SettingsControl[] {
  // hooks
  const { widget, settings, widgetState } = useScreenshotContext();
  const isMultiProduct = !!widgetState?.multiProductActive;
  // Only a box plot with a clipped Data Range shows the subtitle the toggle hides.
  const hasDataRangeSubtitle =
    !!widgetState?.dataRange && widgetState.dataRange !== "all";
  const dashboardWidgets = useDashboardScreenshotStore((s) => s.activeWidgets);
  const dashboardHasChart = dashboardWidgets.some((w) => CHART_WIDGET_TYPES.has(w.type));
  const userSettings = useUserSettings();
  const userBorderType = userSettings?.userDashboardSettings?.borderType;

  const ChartSettingsGroups = {
    title: "Chart Title",
    axis: "Chart Axis",
    legend: "Chart Legend",
    advanced: "Advanced Chart Settings"
  };

  const numericChartType = widgetState?.request?.chartSetting?.chartType;
  // Rig/Frac Activity vs Time is a non-multi-product chart with a real secondary
  // (right) y-axis — Frac Crew Count — so it drives the same per-axis title +
  // gridline control (AxisSettingsList) the multi-product path uses.
  const isRigFracActivityChart = isRigFracActivityRequestChartType(
    widgetState?.request?.chartType
  );
  const usesPerAxisControls = isMultiProduct || isRigFracActivityChart;
  // StackedBarHorizontal shares StackedBar's wire chartType id, but its x-axis
  // is numeric — rotating x labels makes no sense for it, so it's excluded here.
  const isStackedBarHorizontal = IS_STACKED_BAR_HORIZONTAL_SCREENSHOT(
    numericChartType,
    widgetState?.options?.yAxis
  );
  const isXRotationChart =
    numericChartType === ALL_CHART_TYPES.BoxPlot.id ||
    numericChartType === ALL_CHART_TYPES.Mosaic.id ||
    (numericChartType === ALL_CHART_TYPES.StackedBar.id && !isStackedBarHorizontal);

  const layout = widgetState?.response?.layout;

  function axisKeys(i: number) {
    const n = i === 0 ? "" : `${i + 1}`;
    return {
      xSettings: `xAxis${n}TitleText`,
      ySettings: `yAxis${n}TitleText`,
      xMajor: `gridShowX${n}Lines`,
      xMinor: `gridShowX${n}MinorLines`,
      yMajor: `gridShowY${n}Lines`,
      yMinor: `gridShowY${n}MinorLines`
    };
  }

  const optionsYAxis =
    isMultiProduct && Array.isArray(widgetState?.options?.yAxis)
      ? (widgetState.options.yAxis as Array<{ position?: string }>)
      : [];

  const sortedYAxisIndices = optionsYAxis.length
    ? getSortedYAxisIndices(optionsYAxis)
    : [];

  const sortedXAxisIndices =
    isMultiProduct && Array.isArray(widgetState?.options?.xAxis)
      ? getSortedXAxisIndices(widgetState.options.xAxis)
      : [];

  // The secondary (Frac Crew Count) axis is only drawn when an overlay line
  // exists — the translator sets `yAxis2.show = hasLine`. Read that live flag
  // straight off the rendered options (yAxis[1]) rather than re-deriving it, so
  // the panel never offers an inert title input + no-op gridline toggles for a
  // hidden axis (ECharts suppresses splitLine when the axis has `show: false`).
  const rigFracSecondaryYAxis = Array.isArray(widgetState?.options?.yAxis)
    ? (widgetState.options.yAxis as Array<{ show?: boolean }>)[1]
    : undefined;
  const rigFracHasSecondaryAxis =
    rigFracSecondaryYAxis != null && rigFracSecondaryYAxis.show !== false;
  const rigFracActivityAxes = isRigFracActivityChart
    ? buildRigFracActivityAxes(layout, rigFracHasSecondaryAxis)
    : null;

  const xAxes: AxisEntry[] = isMultiProduct
    ? sortedXAxisIndices.map((axisIdx, rank) => {
        const ax = layout?.xAxis2?.[axisIdx];
        const k = axisKeys(rank);
        return {
          label: ax ? stripAxisLegendSuffix(ax.title) : "",
          isPrimary: rank === 0,
          settingsKey: k.xSettings,
          gridMajorKey: k.xMajor,
          gridMinorKey: k.xMinor
        };
      })
    : rigFracActivityAxes?.xAxes ?? [];
  const yAxes: AxisEntry[] = isMultiProduct
    ? sortedYAxisIndices.map((axisIdx, rank) => {
        const ax = layout?.yAxis2?.[axisIdx];
        const k = axisKeys(rank);
        return {
          label: ax ? stripAxisLegendSuffix(ax.title) : "",
          isPrimary: optionsYAxis[axisIdx]?.position !== "right",
          settingsKey: k.ySettings,
          gridMajorKey: k.yMajor,
          gridMinorKey: k.yMinor
        };
      })
    : rigFracActivityAxes?.yAxes ?? [];

  const controls: SettingsControl[] = [];

  const isChart =
    widget?.widgetComponentType === WidgetComponentType.Chart ||
    widget?.widgetComponentType === WidgetComponentType.ChartBeta;
  const isDashboardWithChart =
    widget?.widgetComponentType === WidgetComponentType.Dashboard && dashboardHasChart;

  if (!isChart && !isDashboardWithChart) {
    return controls;
  }

  // In dashboard mode, skip font size preset controls, chart title, axis, and advanced sections
  if (!isDashboardWithChart) {
    controls.push(
      {
        group: SettingsGroups.preset,
        control: (
          <ScreenshotSetting.Item
            key="titleFontSize"
            name="titleFontSize"
            label="Title Size">
            <ScreenshotSetting.Input type="number" min={0} unit="pt" />
          </ScreenshotSetting.Item>
        )
      },
      {
        group: SettingsGroups.preset,
        control: (
          <ScreenshotSetting.Item
            key="axisFontSize"
            name="axisFontSize"
            label="Axis Title Size">
            <ScreenshotSetting.Input type="number" min={0} unit="pt" />
          </ScreenshotSetting.Item>
        )
      },
      {
        group: SettingsGroups.preset,
        control: (
          <ScreenshotSetting.Item
            key="axisLabelFontSize"
            name="axisLabelFontSize"
            label="Axis Labels Size">
            <ScreenshotSetting.Input type="number" min={0} unit="pt" />
          </ScreenshotSetting.Item>
        )
      }
    );
  }

  controls.push(
    {
      group: SettingsGroups.preset,
      control: (
        <ScreenshotSetting.Item
          key="legendTitleSize"
          name="legendTitleSize"
          label="Legend Title Size">
          <ScreenshotSetting.Input type="number" min={0} unit="pt" />
        </ScreenshotSetting.Item>
      )
    },
    {
      group: SettingsGroups.preset,
      control: (
        <ScreenshotSetting.Item
          key="legendFontSize"
          name="legendFontSize"
          label="Legend Label Size">
          <ScreenshotSetting.Input type="number" min={0} unit="pt" />
        </ScreenshotSetting.Item>
      )
    },
    {
      group: SettingsGroups.quick,
      control: (
        <ScreenshotSetting.Item
          key="legendVisible"
          name="legendVisible"
          label="Chart Legend"
          valuePropName="checked">
          <ScreenshotSetting.Input type="boolean" />
        </ScreenshotSetting.Item>
      )
    },
    ...(!isDashboardWithChart && isMultiProduct
      ? [
          {
            group: SettingsGroups.quick,
            control: (
              <ScreenshotSetting.Item
                key="showProductLegend"
                name="showProductLegend"
                label="Product Legend"
                valuePropName="checked">
                <ScreenshotSetting.Input type="boolean" />
              </ScreenshotSetting.Item>
            )
          }
        ]
      : []),
    ...(!isDashboardWithChart
      ? [
          {
            group: SettingsGroups.quick,
            control: (
              <ScreenshotSetting.Item
                key="applyAbbreviations"
                name="applyAbbreviations"
                label="Use Abbreviations"
                valuePropName="checked">
                <ScreenshotSetting.Input type="boolean" />
              </ScreenshotSetting.Item>
            )
          },
          {
            group: SettingsGroups.quick,
            control: (
              <ScreenshotSetting.Item
                key="simplifyCustomFieldNames"
                name="simplifyCustomFieldNames"
                label="Simplify Custom Field Names"
                valuePropName="checked">
                <ScreenshotSetting.Input type="boolean" />
              </ScreenshotSetting.Item>
            )
          }
        ]
      : []),
    ...(!isDashboardWithChart && hasDataRangeSubtitle
      ? [
          {
            group: SettingsGroups.quick,
            control: (
              <ScreenshotSetting.Item
                key="showDataRangeSubtitle"
                name="showDataRangeSubtitle"
                label="Data Range Subtitle"
                valuePropName="checked">
                <ScreenshotSetting.Input type="boolean" />
              </ScreenshotSetting.Item>
            )
          }
        ]
      : []),
    ...(isDashboardWithChart && userBorderType !== "borderless"
      ? [
          {
            group: SettingsGroups.preset,
            control: <WidgetBorderToggle key="widgetBorderVisible" />
          }
        ]
      : []),
    ...(!isDashboardWithChart && !isMultiProduct
      ? [
          {
            group: SettingsGroups.quick,
            control: (
              <ScreenshotSetting.Item
                key="useLegacyTitles"
                name="useLegacyTitles"
                label="Use Legacy Titles"
                valuePropName="checked">
                <ScreenshotSetting.Input type="boolean" />
              </ScreenshotSetting.Item>
            )
          }
        ]
      : []),
    ...(!isDashboardWithChart
      ? [
          {
            group: SettingsGroups.quick,
            control: (
              <ScreenshotSetting.Item
                key="useUppercaseTitles"
                name="useUppercaseTitles"
                label="Use Uppercase Titles"
                valuePropName="checked">
                <ScreenshotSetting.Input type="boolean" />
              </ScreenshotSetting.Item>
            )
          }
        ]
      : []),
    ...(!isDashboardWithChart
      ? [
          {
            group: ChartSettingsGroups.title,
            control: (
              <ScreenshotSetting.Item key="titleText" name="titleText" label="Title">
                <ScreenshotSetting.Input type="text" />
              </ScreenshotSetting.Item>
            )
          },
          {
            group: ChartSettingsGroups.axis,
            control: (
              <ScreenshotSetting.Item
                key="showAxisTitle"
                name="showAxisTitle"
                label="Axis Title"
                valuePropName="checked">
                <ScreenshotSetting.Input type="boolean" />
              </ScreenshotSetting.Item>
            )
          }
        ]
      : [])
  );

  if (!isDashboardWithChart && settings?.showAxisTitle) {
    // Per-axis charts (multi-product + Activity vs Time) render their axis-title
    // inputs inside AxisSettingsList below; only single-axis charts get the flat
    // X-Axis / Y-Axis Label inputs here.
    if (!usesPerAxisControls) {
      controls.push(
        {
          group: ChartSettingsGroups.axis,
          control: (
            <ScreenshotSetting.Item
              key="xAxisTitleText"
              name="xAxisTitleText"
              label="X-Axis Label">
              <ScreenshotSetting.Input type="text" />
            </ScreenshotSetting.Item>
          )
        },
        {
          group: ChartSettingsGroups.axis,
          control: (
            <ScreenshotSetting.Item
              key="yAxisTitleText"
              name="yAxisTitleText"
              label="Y-Axis Label">
              <ScreenshotSetting.Input type="text" />
            </ScreenshotSetting.Item>
          )
        }
      );
    }

    controls.push({
      group: ChartSettingsGroups.axis,
      control: (
        <ScreenshotSetting.Item
          key="axisFontWeight"
          name="axisFontWeight"
          label="Title Font Weight">
          <ScreenshotSetting.Input type="number" step={100} min={100} />
        </ScreenshotSetting.Item>
      )
    });
  }

  if (!isDashboardWithChart) {
    controls.push(
      {
        group: ChartSettingsGroups.axis,
        control: (
          <ScreenshotSetting.Item
            key="showAxisLabels"
            name="showAxisLabels"
            label="Axis Labels"
            valuePropName="checked">
            <ScreenshotSetting.Input type="boolean" />
          </ScreenshotSetting.Item>
        )
      },
      ...(usesPerAxisControls
        ? [
            // Merge-Same-Unit is a multi-product concept only.
            ...(isMultiProduct
              ? [
                  {
                    group: ChartSettingsGroups.axis,
                    control: (
                      <ScreenshotSetting.Item
                        key="mergeSameUnitAxes"
                        name="mergeSameUnitAxes"
                        label="Merge Same Unit Axes"
                        valuePropName="checked">
                        <ScreenshotSetting.Input type="boolean" />
                      </ScreenshotSetting.Item>
                    )
                  }
                ]
              : []),
            {
              group: ChartSettingsGroups.axis,
              control: (
                <AxisSettingsList
                  key="axisSettingsList"
                  xAxes={xAxes}
                  yAxes={yAxes}
                  showAxisTitle={!!settings?.showAxisTitle}
                  // The P/S badge is a multi-product concept; hide it for the
                  // rig/frac Activity axes (Rig Count / Frac Crew Count).
                  showPrimaryBadge={!isRigFracActivityChart}
                />
              )
            }
          ]
        : [
            ...(isXRotationChart
              ? [
                  {
                    group: ChartSettingsGroups.axis,
                    control: (
                      <ScreenshotSetting.Item
                        key="xLabelRotation"
                        name="xLabelRotation"
                        label="X-Label Rotation">
                        <XLabelRotationSelector />
                      </ScreenshotSetting.Item>
                    )
                  }
                ]
              : []),
            {
              group: ChartSettingsGroups.axis,
              control: (
                <ScreenshotSetting.Item
                  key="gridShowXLines"
                  name="gridShowXLines"
                  label="X-Axis Major Lines"
                  valuePropName="checked">
                  <ScreenshotSetting.Input type="boolean" />
                </ScreenshotSetting.Item>
              )
            },
            {
              group: ChartSettingsGroups.axis,
              control: (
                <ScreenshotSetting.Item
                  key="gridShowXMinorLines"
                  name="gridShowXMinorLines"
                  label="X-Axis Minor Lines"
                  valuePropName="checked">
                  <ScreenshotSetting.Input type="boolean" />
                </ScreenshotSetting.Item>
              )
            },
            {
              group: ChartSettingsGroups.axis,
              control: (
                <ScreenshotSetting.Item
                  key="gridShowYLines"
                  name="gridShowYLines"
                  label="Y-Axis Major Lines"
                  valuePropName="checked">
                  <ScreenshotSetting.Input type="boolean" />
                </ScreenshotSetting.Item>
              )
            },
            {
              group: ChartSettingsGroups.axis,
              control: (
                <ScreenshotSetting.Item
                  key="gridShowYMinorLines"
                  name="gridShowYMinorLines"
                  label="Y-Axis Minor Lines"
                  valuePropName="checked">
                  <ScreenshotSetting.Input type="boolean" />
                </ScreenshotSetting.Item>
              )
            }
          ])
    );
  }

  if (settings?.legendVisible) {
    controls.push(
      {
        group: ChartSettingsGroups.legend,
        control: (
          <ScreenshotSetting.Item
            key="legendShowCounts"
            name="legendShowCounts"
            label="Show Count"
            valuePropName="checked">
            <ScreenshotSetting.Input type="boolean" />
          </ScreenshotSetting.Item>
        )
      },
      ...(!isDashboardWithChart
        ? [
            {
              group: ChartSettingsGroups.legend,
              control: (
                <ScreenshotSetting.Item
                  key="legendShowTypeWellRescat"
                  name="legendShowTypeWellRescat"
                  label="Show Rescat"
                  valuePropName="checked">
                  <ScreenshotSetting.Input type="boolean" />
                </ScreenshotSetting.Item>
              )
            }
          ]
        : []),
      {
        group: ChartSettingsGroups.legend,
        control: (
          <ScreenshotSetting.Item
            key="legendBorderVisible"
            name="legendBorderVisible"
            label="Legend Border"
            valuePropName="checked">
            <ScreenshotSetting.Input type="boolean" />
          </ScreenshotSetting.Item>
        )
      },
      {
        group: ChartSettingsGroups.legend,
        control: (
          <ScreenshotSetting.Item
            key="isWrappingText"
            name="isWrappingText"
            label="Text Wrap"
            valuePropName="checked">
            <ScreenshotSetting.Input type="boolean" />
          </ScreenshotSetting.Item>
        )
      },
      {
        group: ChartSettingsGroups.legend,
        control: (
          <ScreenshotSetting.Item
            key="legendOpacity"
            name="legendOpacity"
            label="Opacity">
            <ScreenshotSetting.Input type="number" step={0.1} min={0} max={1} />
          </ScreenshotSetting.Item>
        )
      }
    );
  }

  if (!isDashboardWithChart) {
    // Reuse the shared font-family control (also used by SAM/MPC) instead of a
    // duplicate inline Select, so width + immediate-commit behavior stays in sync.
    controls.push(
      ...buildControls([fontFamilyControl(ChartSettingsGroups.advanced)], {
        isDashboard: false
      })
    );
  }

  return controls;
}

export default useChartScreenshotSettingsControls;
